import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useSnackbar } from "notistack";

const GodModeContext = createContext(null);

export const useGodMode = () => {
  const context = useContext(GodModeContext);
  if (!context) {
    throw new Error("useGodMode must be used within a GodModeProvider");
  }
  return context;
};

export function GodModeProvider({ children }) {
  const [isGodMode, setIsGodMode] = useState(false);
  const [impersonatedSchool, setImpersonatedSchool] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const storedSchool = localStorage.getItem("impersonating_school");
    if (storedSchool) {
      try {
        const school = JSON.parse(storedSchool);
        setImpersonatedSchool(school);
        setIsGodMode(true);
      } catch (e) {
        localStorage.removeItem("impersonating_school");
      }
    }
  }, []);

  const enterGodMode = useCallback((school) => {
    localStorage.setItem("impersonating_school", JSON.stringify(school));
    setImpersonatedSchool(school);
    setIsGodMode(true);
    enqueueSnackbar(`Now acting as admin for ${school.name}`, { variant: "success" });
  }, [enqueueSnackbar]);

  const exitGodMode = useCallback((navigate) => {
    const schoolId = impersonatedSchool?._id;
    localStorage.removeItem("impersonating_school");
    setImpersonatedSchool(null);
    setIsGodMode(false);
    enqueueSnackbar("Exited God Mode", { variant: "info" });
    if (navigate && schoolId) {
      navigate(`/flight-schools/${schoolId}`);
    } else if (navigate) {
      navigate("/flight-schools");
    }
  }, [impersonatedSchool, enqueueSnackbar]);

  const getImpersonatedSchoolId = useCallback(() => {
    return impersonatedSchool?._id || null;
  }, [impersonatedSchool]);

  const value = {
    isGodMode,
    impersonatedSchool,
    enterGodMode,
    exitGodMode,
    getImpersonatedSchoolId,
  };

  return (
    <GodModeContext.Provider value={value}>
      {children}
    </GodModeContext.Provider>
  );
}
